import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import LeaderboardModal from './LeaderboardModal';

function QuizBattles({ initialBattleId }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeBattles, setActiveBattles] = useState([]);
  const [pastBattles, setPastBattles] = useState([]);
  const [selectedBattle, setSelectedBattle] = useState(null);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [participantCounts, setParticipantCounts] = useState({});
  const [completedBattleIds, setCompletedBattleIds] = useState([]);

  useEffect(() => {
    fetchBattles();
    fetchCompletedBattles();
  }, []);

  // Add a useEffect to scroll to a specific battle if battleId is in the URL query params
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const battleId = initialBattleId || urlParams.get('battleId');
    if (battleId) {
      // Wait for battles to be rendered
      requestAnimationFrame(() => {
        const battleElement = document.getElementById(battleId);
        if (battleElement) {
          battleElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
          // Optionally add a temporary highlight class
          battleElement.classList.add('highlight-battle');
          setTimeout(() => {
            battleElement.classList.remove('highlight-battle');
          }, 2000); // Remove highlight after 2 seconds
        }
      });
    }
  }, [activeBattles, pastBattles]); // Depend on battles state to ensure elements exist

  const fetchParticipantCount = async (battleId) => {
    try {
      const participantsRef = collection(db, 'public-quiz-participants');
      const q = query(participantsRef, where('battleId', '==', battleId), where('completed', '==', true));
      const snapshot = await getDocs(q);
      return snapshot.size;
    } catch (error) {
      console.error(`Error fetching participant count for battle ${battleId}:`, error);
      return 0;
    }
  };

  const fetchCompletedBattles = async () => {
    if (!auth.currentUser) return;
    try {
      const participantsRef = collection(db, 'public-quiz-participants');
      const q = query(participantsRef, where('userId', '==', auth.currentUser.uid), where('completed', '==', true));
      const snapshot = await getDocs(q);
      const completedIds = snapshot.docs.map(doc => doc.data().battleId);
      console.log('Completed battles for user:', completedIds);
      setCompletedBattleIds(completedIds);
    } catch (error) {
      console.error('Error fetching completed battles:', error);
    }
  };

  const fetchBattles = async () => {
    try {
      setLoading(true);
      const now = new Date();

      // Fetch active battles
      const activeQuery = query(
        collection(db, 'quiz_battles'),
        where('endDate', '>', now),
        orderBy('endDate', 'asc')
      );
      const activeSnapshot = await getDocs(activeQuery);
      const activeBattlesList = activeSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setActiveBattles(activeBattlesList);

      // Fetch past battles
      const pastQuery = query(
        collection(db, 'quiz_battles'),
        where('endDate', '<=', now),
        orderBy('endDate', 'desc'),
        limit(5)
      );
      const pastSnapshot = await getDocs(pastQuery);
      const pastBattlesList = pastSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPastBattles(pastBattlesList);

      // Fetch participant counts for all fetched battles
      const allBattles = [...activeBattlesList, ...pastBattlesList];
      const counts = {};
      for (const battle of allBattles) {
        counts[battle.id] = await fetchParticipantCount(battle.id);
      }
      setParticipantCounts(counts);

    } catch (error) {
      console.error('Error fetching battles:', error);
      setError('Failed to load quiz battles');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinBattle = async (battleId) => {
    try {
      // Navigate to the quiz with battle mode enabled
      window.location.href = `/take-battle/${battleId}?mode=battle`;
    } catch (error) {
      console.error('Error joining battle:', error);
      setError('Failed to join battle');
    }
  };

  const handleViewLeaderboard = (battle) => {
    setSelectedBattle(battle);
    setShowLeaderboard(true);
  };

  const handleShareBattle = async (battleId, battleTitle) => {
    // Changed URL to point to the quiz battles page with battleId as a query param
    const shareUrl = `${window.location.origin}/quiz-repository?battleId=${battleId}`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: `Join the Quiz Battle: ${battleTitle}`,
          text: `Compete in the "${battleTitle}" quiz battle! Click the link to join:`,
          url: shareUrl,
        });
        console.log('Battle shared successfully');
      } else {
        // Fallback for browsers that do not support navigator.share
        await navigator.clipboard.writeText(shareUrl);
        alert('Share link copied to clipboard!');
        console.log('Share link copied to clipboard:', shareUrl);
      }
    } catch (error) {
      console.error('Error sharing battle:', error);
      alert('Failed to share battle link.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[rgb(130,88,18)]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500 text-center p-4">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Active Battles */}
      <section>
        <h2 className="text-2xl font-bold mb-4">Active Battles</h2>
        {activeBattles.length === 0 ? (
          <p className="text-gray-500">No active battles at the moment.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {activeBattles.map(battle => (
              <div
                key={battle.id}
                id={battle.id}
                className="bg-white p-6 rounded-lg shadow-lg flex flex-col"
              >
                <h3 className="text-xl font-semibold mb-2">{battle.title}</h3>
                <p className="text-gray-600 mb-4 flex-grow">{battle.description}</p>
                <div className="space-y-2 mb-4">
                  <p className="text-sm">
                    <span className="font-medium">Subject:</span> {battle.subject}
                  </p>
                  <p className="text-sm">
                    <span className="font-medium">Difficulty:</span> {battle.difficulty}
                  </p>
                  <p className="text-sm">
                    <span className="font-medium">Questions:</span> {battle.questionCount}
                  </p>
                  <p className="text-sm">
                    <span className="font-medium">Time Limit:</span> {battle.timeLimit} minutes
                  </p>
                  <p className="text-sm">
                    <span className="font-medium">Ends:</span>{' '}
                    {battle.endDate.toDate().toLocaleString()}
                  </p>
                  {completedBattleIds.includes(battle.id) && (
                    <p className="text-sm">
                      <span className="font-medium">Participants:</span> {participantCounts[battle.id] !== undefined ? participantCounts[battle.id] : 'Loading...'}
                    </p>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4 mt-auto">
                  {completedBattleIds.includes(battle.id) ? (
                    <button
                      disabled
                      className="flex-1 bg-gray-400 text-white py-2 px-4 rounded-md cursor-not-allowed"
                    >
                      Completed
                    </button>
                  ) : (
                    <button
                      onClick={() => handleJoinBattle(battle.id)}
                      className="flex-1 bg-[rgb(130,88,18)] text-white py-2 px-4 rounded-md hover:bg-[rgb(110,68,0)] transition-colors"
                    >
                      Join Battle
                    </button>
                  )}
                  <button
                    onClick={() => handleViewLeaderboard(battle)}
                    className="flex-1 border border-[rgb(130,88,18)] text-[rgb(130,88,18)] py-2 px-4 rounded-md hover:bg-[rgb(130,88,18)] hover:text-white transition-colors"
                    disabled={!completedBattleIds.includes(battle.id)}
                    style={{ cursor: completedBattleIds.includes(battle.id) ? 'pointer' : 'not-allowed' }}
                  >
                    Leaderboard
                  </button>
                  <button
                    onClick={() => handleShareBattle(battle.id, battle.title)}
                    className="w-full sm:w-auto p-2 rounded-md hover:bg-gray-200 transition-colors flex items-center justify-center"
                    aria-label="Share Battle"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.72l4.94 2.47C13.023 14.63 15 16.573 15 19a3 3 0 10-3-3c-.046 0-.09-.004-.133-.012l-4.94-2.47a3 3 0 100-4.72l4.94-2.47c.043-.008.087-.012.133-.012a3 3 0 103-3z" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Past Battles */}
      <section>
        <h2 className="text-2xl font-bold mb-4">Past Battles</h2>
        {pastBattles.length === 0 ? (
          <p className="text-gray-500">No past battles available.</p>
        ) : (
          <div className="space-y-4">
            {pastBattles
              .filter(battle => completedBattleIds.includes(battle.id))
              .map(battle => (
              <div key={battle.id} className="bg-white p-4 rounded-lg shadow flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">{battle.title}</h3>
                  <p className="text-sm text-gray-600">
                    Ended: {battle.endDate.toDate().toLocaleString()}
                  </p>
                  {completedBattleIds.includes(battle.id) && (
                    <p className="text-sm">
                      <span className="font-medium">Participants:</span> {participantCounts[battle.id] !== undefined ? participantCounts[battle.id] : 'Loading...'}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => handleViewLeaderboard(battle)}
                  className="text-[rgb(130,88,18)] hover:text-[rgb(110,68,0)] transition-colors"
                >
                  View Results
                </button>
              </div>
            ))}
            {pastBattles.filter(battle => completedBattleIds.includes(battle.id)).length === 0 && (
              <p className="text-gray-500">You haven't participated in any past battles.</p>
            )}
          </div>
        )}
      </section>

      {/* Leaderboard Modal */}
      {showLeaderboard && selectedBattle && (
        <LeaderboardModal
          quizId={selectedBattle.id}
          quizName={selectedBattle.title}
          onClose={() => setShowLeaderboard(false)}
        />
      )}
    </div>
  );
}

export default QuizBattles; 